import { useQuery } from '@tanstack/react-query';
import { TopicRequestBuilder } from '../../utils/TopicRequestBuilder';
import axios from 'axios';
import { useBaseTopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/useBaseTopicRequestBuilder';
import { useSplineComparisonDateRange, useSplineMainDateRange } from 'src/utils/chartUtils';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';

export interface GetBrandClassificationMetricsResponseRecord {
  isBranded: boolean;
  weekId: number;
  totalTraffic: number;
  organicTraffic: number;
  paidTraffic: number;
  totalTrafficKeywordCount: number;
  organicTrafficKeywordCount: number;
}
export interface GetBrandClassificationMetricsResponse {
  records: GetBrandClassificationMetricsResponseRecord[];
}

/**
 * Fetches the traffic metrics by week for topics/topic groups
 */
export const useBrandClassificationSplineMetrics = ({
  buildRequest
}: {
  buildRequest?: (baseRequest: TopicRequestBuilder, isComparison: boolean) => TopicRequestBuilder;
} = {}) => {
  const { startWeek, endWeek } = useSplineMainDateRange();
  const { comparisonEndWeek, comparisonStartWeek } = useSplineComparisonDateRange();
  const entityType = useMainEntityType();

  const baseRequest = useBaseTopicRequestBuilder();

  const percentTrafficRequest = baseRequest
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .apply((builder) => buildRequest?.(builder, false) ?? builder)
    .build();

  const percentTrafficRequestComparison = new TopicRequestBuilder(percentTrafficRequest)
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek)
    .apply((builder) => buildRequest?.(builder, true) ?? builder)
    .build();

  const brandClassificationQueryKey =
    entityType === 'topicgroup' ? 'GetTopicgroupClassificationMetrics' : 'GetBrandClassificationMetrics';
  const { data, isLoading } = useQuery({
    queryKey: [
      TOPIC_QUERY_KEYS.all,
      brandClassificationQueryKey,
      percentTrafficRequest,
      percentTrafficRequestComparison
    ],
    queryFn: async () => {
      const endpoint = `/api/beacontopics/${brandClassificationQueryKey}`;
      const [{ data: response }, { data: comparisonResponse }] = await Promise.all([
        axios.post<GetBrandClassificationMetricsResponse>(endpoint, percentTrafficRequest),
        axios.post<GetBrandClassificationMetricsResponse>(endpoint, percentTrafficRequestComparison)
      ]);

      return [response, comparisonResponse];
    },
    refetchOnWindowFocus: false
  });

  const response = data ?? [{ records: [] }, { records: [] }];

  return {
    response,
    isLoading
  };
};
